/** Auth navigation state used to reopen a booking dialog after login. */
export type AuthBookingState = {
  openBookingRoomId?: string;
  roomAvailableFromTime?: string;
  durationInMinutes?: number;
  openRoomBooking?: boolean;
  openTutorBooking?: boolean;
  lessonOptionId?: string | null;
  startTime?: string;
};

export const TUTOR_BOOKING_RESUME_OPEN_PARAM = 'openTutorBooking';
export const TUTOR_BOOKING_RESUME_LESSON_PARAM = 'lessonOptionId';
export const TUTOR_BOOKING_RESUME_START_PARAM = 'startTime';
export const ROOM_BOOKING_RESUME_OPEN_PARAM = 'openRoomBooking';
export const ROOM_BOOKING_RESUME_ROOM_ID_PARAM = 'openBookingRoomId';

export function appendBookingResumeSearchParams(
  url: URL,
  bookingState: AuthBookingState | undefined,
): void {
  const roomAvailableFromTime = bookingState?.roomAvailableFromTime;
  const durationInMinutes = bookingState?.durationInMinutes;
  if (roomAvailableFromTime) {
    url.searchParams.set('roomAvailableFromTime', roomAvailableFromTime);
  }
  if (durationInMinutes != null) {
    url.searchParams.set('durationInMinutes', String(durationInMinutes));
  }
  if (bookingState?.openRoomBooking) {
    url.searchParams.set(ROOM_BOOKING_RESUME_OPEN_PARAM, '1');
  }
  if (bookingState?.openBookingRoomId) {
    url.searchParams.set(ROOM_BOOKING_RESUME_ROOM_ID_PARAM, bookingState.openBookingRoomId);
  }
  if (bookingState?.openTutorBooking) {
    url.searchParams.set(TUTOR_BOOKING_RESUME_OPEN_PARAM, '1');
  }
  if (bookingState?.lessonOptionId) {
    url.searchParams.set(TUTOR_BOOKING_RESUME_LESSON_PARAM, bookingState.lessonOptionId);
  }
  if (bookingState?.startTime) {
    url.searchParams.set(TUTOR_BOOKING_RESUME_START_PARAM, bookingState.startTime);
  }
}

export function readTutorBookingResumeFromSearchParams(searchParams: URLSearchParams): {
  open: boolean;
  lessonOptionId: string | null;
  startTime: string | null;
} {
  return {
    open: searchParams.get(TUTOR_BOOKING_RESUME_OPEN_PARAM) === '1',
    lessonOptionId: searchParams.get(TUTOR_BOOKING_RESUME_LESSON_PARAM),
    startTime: searchParams.get(TUTOR_BOOKING_RESUME_START_PARAM),
  };
}

export function stripTutorBookingResumeSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(TUTOR_BOOKING_RESUME_OPEN_PARAM);
  next.delete(TUTOR_BOOKING_RESUME_LESSON_PARAM);
  next.delete(TUTOR_BOOKING_RESUME_START_PARAM);
  return next;
}

export function readRoomBookingResumeFromSearchParams(searchParams: URLSearchParams): {
  open: boolean;
  roomId: string | null;
  roomAvailableFromTime: string | null;
  durationInMinutes: number | null;
} {
  const durationRaw = searchParams.get('durationInMinutes');
  const durationParsed = durationRaw != null ? Number(durationRaw) : NaN;
  return {
    open: searchParams.get(ROOM_BOOKING_RESUME_OPEN_PARAM) === '1',
    roomId: searchParams.get(ROOM_BOOKING_RESUME_ROOM_ID_PARAM),
    roomAvailableFromTime: searchParams.get('roomAvailableFromTime'),
    durationInMinutes: Number.isFinite(durationParsed) ? durationParsed : null,
  };
}

export function stripRoomBookingResumeSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(ROOM_BOOKING_RESUME_OPEN_PARAM);
  next.delete(ROOM_BOOKING_RESUME_ROOM_ID_PARAM);
  next.delete('roomAvailableFromTime');
  next.delete('durationInMinutes');
  return next;
}
